import { useFilterBonusRef } from "@/client/stores/filter";
import { useDialog } from "@/client/stores/dialog";
import { usefilterUser } from "@/client/services/user/filter-user";
import { FilterBonusRefType } from "@reactive-resume/dto";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  Input,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";

import { useRef } from "react";
import { useForm } from "react-hook-form";
import { SelectFormField } from "../_components/select-form-fields";

type StringFilterCondition = "startsWith" | "contains" | "equals" | "endsWith";
type DateFilterCondition = "gt" | "gte" | "equals" | "lt" | "lte";

export type BonusFormType = {
  userId?: {
    condition: StringFilterCondition;
    value: string;
  };
  amount?: {
    condition: "equals" | "gt" | "gte" | "lt" | "lte";
    value: string;
  };
  createdAt?: {
    condition: DateFilterCondition;
    value: string;
  };
};

const ObjectConversionToFilterType = (object: BonusFormType) => {
  const filter: FilterBonusRefType = {};

  Object.entries(object).forEach(([key, field]) => {
    if (!field || !field.value) return;

    if (key === "amount") {
      filter.amount = {
        [field.condition]: Number(field.value),
      };
      return;
    }

    filter[key as keyof FilterBonusRefType] = {
      [field.condition]: field.value,
    } as never;
  });

  return filter;
};

export const FilterBonusRefDialog = () => {
  type FormValue = BonusFormType;

  const formRef = useRef<HTMLFormElement>(null);
  const { replaceFilter } = useFilterBonusRef();
  const { filterUsers } = usefilterUser(false);
  const { isOpen, close } = useDialog("filter-bonus");

  const form = useForm<FormValue>({
    defaultValues: {
      userId: {
        condition: "equals",
        value: "",
      },
      amount: {
        condition: "equals",
        value: "",
      },
      createdAt: {
        condition: "equals",
        value: "",
      },
    },
  });

  const OnSubmit = (data: FormValue) => {
    try {
      data.userId!.condition = "equals";
      const filter = ObjectConversionToFilterType(data);
      replaceFilter(filter);
      form.reset();
      close();
    } catch {
      form.reset();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Filtrer les bonus de parrainage</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            ref={formRef}
            onSubmit={form.handleSubmit(OnSubmit)}
            className="flex flex-col gap-y-4"
          >
            <div className="flex flex-row gap-2">
              <div className="flex w-1/2 flex-col gap-2">
                <SelectFormField
                  label="Utilisateur"
                  form={form}
                  condition="String"
                  nameField="userId.condition"
                  disabled={true}
                />
                <FormField
                  name="userId.value"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <Select value={field.value} onValueChange={(value) => field.onChange(value)}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Sélectionner un client" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <ScrollArea orientation="vertical" className="h-[300px]">
                            {filterUsers?.data
                              .slice()
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((item) => (
                                <SelectItem key={item.id} value={item.id}>
                                  <span className="font-bold">{item.name}</span> ||{" "}
                                  <span className="text-moncvpro-secondary text-lg font-bold">
                                    {item.email}
                                  </span>
                                </SelectItem>
                              ))}
                          </ScrollArea>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex flex-row gap-2">
              <div className="flex w-1/2 flex-col gap-2">
                <SelectFormField
                  label="Montant"
                  form={form}
                  condition="Number"
                  nameField="amount.condition"
                />
                <Input {...form.register("amount.value")} type="number" min={0} />
              </div>
              <div className="flex w-1/2 flex-col gap-2">
                <SelectFormField
                  label="Créé le"
                  form={form}
                  condition="Date"
                  nameField="createdAt.condition"
                />
                <Input {...form.register("createdAt.value")} type="date" />
              </div>
            </div>

            <div className="flex justify-center gap-x-3">
              <Button type="submit" className="bg-moncvpro-secondary">
                {"Valider"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
      <DialogFooter></DialogFooter>
    </Dialog>
  );
};
